/**
 * Income vocabulary. The structured income ledger is the app's source of truth for income
 * (decision Q3); the workbook's salary cell and free-text "Extra Entrate" list are only an import
 * source and, later, a mirror target.
 */

export const INCOME_TYPES = ["salary", "bonus", "reimbursement", "gift", "other"] as const;
export type IncomeType = (typeof INCOME_TYPES)[number];

export const INCOME_STATUSES = ["confirmed", "void"] as const;
export type IncomeStatus = (typeof INCOME_STATUSES)[number];

export const INCOME_SOURCES = ["manual", "excel_import"] as const;
export type IncomeSource = (typeof INCOME_SOURCES)[number];

/** Income is booked in the month it is received unless the user overrides it; `legacy` = imported. */
export const INCOME_ACCOUNTING_RULES = ["same_month", "manual_override", "legacy"] as const;
export type IncomeAccountingRule = (typeof INCOME_ACCOUNTING_RULES)[number];

/**
 * Which workbook line an income type belongs to: the monthly `E6` salary cell ("Stipendio") or
 * the "Extra Entrate" list (`E7`). Used for reporting parity with the workbook and, later, mirroring.
 */
export type WorkbookIncomeLine = "salary" | "extra";

export function workbookIncomeLine(type: IncomeType): WorkbookIncomeLine {
  return type === "salary" ? "salary" : "extra";
}

/**
 * Deterministic type for a note imported from the workbook's "Extra Entrate" list. Only explicit
 * leading words are recognised; everything else is `other` and can be changed by the user.
 */
export function incomeTypeFromWorkbookNote(note: string): IncomeType {
  const n = note.trim().toLocaleLowerCase("it-IT");
  if (n.startsWith("rimbors")) return "reimbursement";
  if (n.startsWith("regalo")) return "gift";
  return "other";
}
